import type { ExamScaledReport, IeltsBandReport, SatScoreReport } from '@examing/types';

export type CategoryScoreTier = 'EXCELLENT' | 'GOOD' | 'AVERAGE' | 'NEEDS_WORK';

export type CategoryColorTheme = 'emerald' | 'gold' | 'blue' | 'purple' | 'amber' | 'rose';

export interface CategoryScoreResult {
  categoryType:
    | 'MILLIY_SERTIFIKAT'
    | 'IELTS'
    | 'SAT'
    | 'DTM_SINGLE'
    | 'DTM_MOCK'
    | 'ATTESTATSIYA'
    | 'LANGUAGES'
    | 'GENERAL';
  /** The category's own grade (A+, Band 7.5, 1-toifa…); null when it would only repeat the percentage. */
  scoreDisplay: string | null;
  title: string;
  subtitle?: string;
  tier: CategoryScoreTier;
  colorTheme: CategoryColorTheme;
  percentage: number;
  passed: boolean | null;
  feedbackMessage: string;
  goalComparison?: {
    userTarget: string;
    isExceeded: boolean;
    isMet: boolean;
    message: string;
  };
}

export interface EstimateScoreInput {
  percentage: number;
  score?: number;
  maxScore?: number;
  passed?: boolean | null;
  category?: string | null;
  title?: string | null;
  tags?: string[] | null;
  subjectConfigsCount?: number;
  scaledReport?: ExamScaledReport | null;
  userGoalLevel?: string | null;
}

const FULL_MOCK_KEYWORDS = [
  'mock',
  'blok',
  'block',
  'aralash',
  'umumiy',
  '189',
  'namunaviy',
  'toliq',
  'полный',
  'пробный',
];

const MS_RANK: Record<string, number> = {
  A_PLUS: 6,
  A: 5,
  B_PLUS: 4,
  B: 3,
  C_PLUS: 2,
  C: 1,
  NONE: 0,
};

const MS_LABELS: Record<string, string> = {
  A_PLUS: 'A+ daraja',
  A: 'A daraja',
  B_PLUS: 'B+ daraja',
  B: 'B daraja',
  C_PLUS: 'C+ daraja',
  C: 'C daraja',
};

function normalizeCategory(raw?: string | null): string {
  const lower = (raw ?? '').trim().toLowerCase();
  if (['milliy-sertifikat', 'milliy sertifikat', 'milliysertifikat', 'milliy'].includes(lower)) {
    return 'milliy-sertifikat';
  }
  if (['attestation', 'attestatsiya'].includes(lower)) {
    return 'attestation';
  }
  if (['dtm'].includes(lower)) {
    return 'dtm';
  }
  if (['ielts'].includes(lower)) {
    return 'ielts';
  }
  if (['sat'].includes(lower)) {
    return 'sat';
  }
  if (['languages', 'til', 'tillar'].includes(lower)) {
    return 'languages';
  }
  return lower;
}

function isDtmFullMock(input: EstimateScoreInput): boolean {
  if ((input.subjectConfigsCount ?? 0) >= 3) return true;
  const text = `${input.title ?? ''} ${(input.tags ?? []).join(' ')}`.toLowerCase();
  return FULL_MOCK_KEYWORDS.some((kw) => text.includes(kw));
}

function compareMilliySertifikatGoal(
  achievedKey: string,
  userTarget?: string | null,
): CategoryScoreResult['goalComparison'] | undefined {
  if (!userTarget || !MS_RANK[userTarget]) return undefined;
  const userRank = MS_RANK[userTarget];
  const achievedRank = MS_RANK[achievedKey] ?? 0;
  const targetLabel = MS_LABELS[userTarget] ?? userTarget;

  if (achievedRank > userRank) {
    return {
      userTarget: targetLabel,
      isExceeded: true,
      isMet: true,
      message: `Maqsadingizdan ham yuqori natija! Siz ${targetLabel}ni maqsad qilgandingiz, natijangiz esa yuqoriroq bo'ldi! 🚀`,
    };
  }
  if (achievedRank === userRank) {
    return {
      userTarget: targetLabel,
      isExceeded: false,
      isMet: true,
      message: `Maqsad zabt etildi! Siz kutilgan ${targetLabel}ni qo'lga kiritdingiz! 🎯`,
    };
  }
  return {
    userTarget: targetLabel,
    isExceeded: false,
    isMet: false,
    message: `Maqsadingiz (${targetLabel})ga erishish uchun yana ozgina amaliyot kerak! 💪`,
  };
}

export function estimateCategoryScore(input: EstimateScoreInput): CategoryScoreResult {
  const percentage = Math.max(0, Math.min(100, Math.round(input.percentage)));
  const canonicalCategory = normalizeCategory(input.category);

  // 1. Standardized IELTS
  if (input.scaledReport?.standard === 'IELTS' || canonicalCategory === 'ielts') {
    const report = input.scaledReport as IeltsBandReport | undefined;
    const band = report?.overall ?? null;
    const bandStr = band !== null ? band.toFixed(1) : (percentage >= 80 ? '7.5' : percentage >= 65 ? '6.5' : '5.5');
    const cefr = report?.cefr ?? (percentage >= 85 ? 'C2' : percentage >= 75 ? 'C1' : percentage >= 60 ? 'B2' : 'B1');
    const numericBand = Number(bandStr);
    const tier: CategoryScoreTier = numericBand >= 7.5 ? 'EXCELLENT' : numericBand >= 6.5 ? 'GOOD' : numericBand >= 5.5 ? 'AVERAGE' : 'NEEDS_WORK';

    let goalComp: CategoryScoreResult['goalComparison'];
    if (input.userGoalLevel) {
      const targetNumeric = Number(input.userGoalLevel);
      if (!Number.isNaN(targetNumeric)) {
        const isMet = numericBand >= targetNumeric;
        const isExceeded = numericBand > targetNumeric;
        goalComp = {
          userTarget: `Band ${targetNumeric}`,
          isExceeded,
          isMet,
          message: isExceeded
            ? `Maqsadingizdan ham yuqori! Band ${targetNumeric} o'rniga Band ${bandStr} oldingiz! 🚀`
            : isMet
              ? `Maqsadga erishildi! Kutilgan Band ${targetNumeric} qo'lga kiritildi! 🎯`
              : `Maqsadingiz (Band ${targetNumeric})gacha ozgina qoldi. Yana bitta mashq! 💪`,
        };
      }
    }

    return {
      categoryType: 'IELTS',
      scoreDisplay: `Band ${bandStr}`,
      title: `IELTS Band ${bandStr}`,
      subtitle: `CEFR ${cefr} darajasi`,
      tier,
      colorTheme: tier === 'EXCELLENT' ? 'purple' : tier === 'GOOD' ? 'emerald' : 'blue',
      percentage,
      passed: input.passed ?? true,
      feedbackMessage: numericBand >= 7.0
        ? 'Ajoyib natija! Xalqaro universitetlar va grantlar uchun yuqori IELTS darajasi!'
        : 'Yaxshi ko‘rsatkich! Muntazam testlar bilan ballingizni yanada oshirishingiz mumkin.',
      goalComparison: goalComp,
    };
  }

  // 2. Standardized SAT
  if (input.scaledReport?.standard === 'SAT' || canonicalCategory === 'sat') {
    const report = input.scaledReport as SatScoreReport | undefined;
    const total = report?.total ?? Math.round(400 + (percentage / 100) * 1200);
    const tier: CategoryScoreTier = total >= 1400 ? 'EXCELLENT' : total >= 1250 ? 'GOOD' : total >= 1100 ? 'AVERAGE' : 'NEEDS_WORK';

    return {
      categoryType: 'SAT',
      scoreDisplay: `${total}`,
      title: `SAT ${total} ball`,
      subtitle: total >= 1400 ? 'Top universitetlar uchun ajoyib ko‘rsatkich' : 'Yaxshi boshlanish',
      tier,
      colorTheme: tier === 'EXCELLENT' ? 'gold' : 'blue',
      percentage,
      passed: input.passed ?? true,
      feedbackMessage: total >= 1350
        ? 'Daxshatli natija! Xalqaro nufuzli oliygohlar talabiga mos yuqori ball!'
        : 'Yaxshi natija! Har bir bo‘lim ustida ishlab, 1400+ natijaga chiqishingiz mumkin.',
    };
  }

  // 3. Milliy Sertifikat
  if (canonicalCategory === 'milliy-sertifikat') {
    if (percentage >= 86) {
      return {
        categoryType: 'MILLIY_SERTIFIKAT',
        scoreDisplay: 'A+',
        title: 'A+ daraja',
        subtitle: 'OTMga 100% imtiyoz (Oliy natija)',
        tier: 'EXCELLENT',
        colorTheme: 'gold',
        percentage,
        passed: true,
        feedbackMessage: 'Daxshatli natija! Siz OTM kirish imtihonida ushbu fandan 100% maksimal ball imtiyoziga egasiz!',
        goalComparison: compareMilliySertifikatGoal('A_PLUS', input.userGoalLevel),
      };
    }
    if (percentage >= 80) {
      return {
        categoryType: 'MILLIY_SERTIFIKAT',
        scoreDisplay: 'A',
        title: 'A daraja',
        subtitle: 'OTMga 100% imtiyoz',
        tier: 'EXCELLENT',
        colorTheme: 'emerald',
        percentage,
        passed: true,
        feedbackMessage: 'Ajoyib g‘alaba! Siz testdan muvaffaqiyatli o‘tib, 100% imtiyozni qo‘lga kiritdingiz!',
        goalComparison: compareMilliySertifikatGoal('A', input.userGoalLevel),
      };
    }
    if (percentage >= 75) {
      return {
        categoryType: 'MILLIY_SERTIFIKAT',
        scoreDisplay: 'B+',
        title: 'B+ daraja',
        subtitle: 'Qisman davlat imtiyozi',
        tier: 'GOOD',
        colorTheme: 'blue',
        percentage,
        passed: true,
        feedbackMessage: 'Yaxshi natija! Siz imtiyozli sertifikat darajasini muvaffaqiyatli zabt etdingiz.',
        goalComparison: compareMilliySertifikatGoal('B_PLUS', input.userGoalLevel),
      };
    }
    if (percentage >= 70) {
      return {
        categoryType: 'MILLIY_SERTIFIKAT',
        scoreDisplay: 'B',
        title: 'B daraja',
        subtitle: 'Qoniqarli sertifikat darajasi',
        tier: 'GOOD',
        colorTheme: 'blue',
        percentage,
        passed: true,
        feedbackMessage: 'Yaxshi natija! Biroz mashq qilsangiz, A darajaga bemalol erishasiz.',
        goalComparison: compareMilliySertifikatGoal('B', input.userGoalLevel),
      };
    }
    if (percentage >= 65) {
      return {
        categoryType: 'MILLIY_SERTIFIKAT',
        scoreDisplay: 'C+',
        title: 'C+ daraja',
        subtitle: 'O‘rta sertifikat darajasi',
        tier: 'AVERAGE',
        colorTheme: 'amber',
        percentage,
        passed: true,
        feedbackMessage: 'Yaxshi harakat, ammo siz bundan ham yuqoriroq natija ko‘rsatishga qodirsiz!',
        goalComparison: compareMilliySertifikatGoal('C_PLUS', input.userGoalLevel),
      };
    }
    if (percentage >= 60) {
      return {
        categoryType: 'MILLIY_SERTIFIKAT',
        scoreDisplay: 'C',
        title: 'C daraja',
        subtitle: 'Minimal sertifikat chegarasi',
        tier: 'AVERAGE',
        colorTheme: 'amber',
        percentage,
        passed: true,
        feedbackMessage: 'Sertifikat chegarasidan o‘tdingiz! Bilimlarni yanada mustahkamlash tavsiya etiladi.',
        goalComparison: compareMilliySertifikatGoal('C', input.userGoalLevel),
      };
    }
    return {
      categoryType: 'MILLIY_SERTIFIKAT',
      scoreDisplay: null,
      title: 'Sertifikat berilmaydi',
      subtitle: 'Minimal ostonaga (60%) yetmadi',
      tier: 'NEEDS_WORK',
      colorTheme: 'rose',
      percentage,
      passed: false,
      feedbackMessage: 'Tushkunlikka tushmang! Zaif mavzular ustida ishlab, keyingi urinishda sertifikat olasiz.',
      goalComparison: compareMilliySertifikatGoal('NONE', input.userGoalLevel),
    };
  }

  // 4. Attestatsiya
  if (canonicalCategory === 'attestation') {
    if (percentage >= 86) {
      return {
        categoryType: 'ATTESTATSIYA',
        scoreDisplay: 'Oliy toifa',
        title: 'Oliy toifa',
        subtitle: 'Oylik ish haqiga +50% ustama',
        tier: 'EXCELLENT',
        colorTheme: 'gold',
        percentage,
        passed: true,
        feedbackMessage: 'Yuksak pedagogik mahorat! Siz oliy toifa talablarini a’lo darajada bajardingiz!',
      };
    }
    if (percentage >= 76) {
      return {
        categoryType: 'ATTESTATSIYA',
        scoreDisplay: '1-toifa',
        title: '1-toifa',
        subtitle: 'Oylik ish haqiga +20% ustama',
        tier: 'GOOD',
        colorTheme: 'emerald',
        percentage,
        passed: true,
        feedbackMessage: 'Ajoyib natija! 1-malaka toifasi uchun yetarli ball to‘plandi.',
      };
    }
    if (percentage >= 66) {
      return {
        categoryType: 'ATTESTATSIYA',
        scoreDisplay: '2-toifa',
        title: '2-toifa',
        subtitle: 'Oylik ish haqiga +15% ustama',
        tier: 'AVERAGE',
        colorTheme: 'blue',
        percentage,
        passed: true,
        feedbackMessage: 'Yaxshi ko‘rsatkich! 2-toifa mezonlari muvaffaqiyatli topshirildi.',
      };
    }
    if (percentage >= 56) {
      return {
        categoryType: 'ATTESTATSIYA',
        scoreDisplay: 'Mutaxassis',
        title: 'Mutaxassis (toifasiz)',
        subtitle: 'Attestatsiyadan o‘tgan',
        tier: 'AVERAGE',
        colorTheme: 'amber',
        percentage,
        passed: true,
        feedbackMessage: 'Mutaxassislik darajasi tasdiqlandi. Yuqori toifalar uchun qo‘shimcha tayyorgarlik ko‘ring.',
      };
    }
    return {
      categoryType: 'ATTESTATSIYA',
      scoreDisplay: null,
      title: 'Toifaga yetmadi',
      subtitle: 'Attestatsiyadan o‘tmadi (minimal 56 ball)',
      tier: 'NEEDS_WORK',
      colorTheme: 'rose',
      percentage,
      passed: false,
      feedbackMessage: 'Attestatsiya ostonasiga biroz yetmadi. Pedagogika va mutaxassislik testlarini takrorlang.',
    };
  }

  // 5. DTM
  if (canonicalCategory === 'dtm') {
    const isMock = isDtmFullMock(input);
    if (isMock) {
      const scaled189 = Math.round((percentage / 100) * 189 * 10) / 10;
      if (scaled189 >= 150) {
        return {
          categoryType: 'DTM_MOCK',
          scoreDisplay: `${scaled189} ball`,
          title: 'Davlat granti ehtimoli yuqori',
          subtitle: '189 balldan yuqori natija',
          tier: 'EXCELLENT',
          colorTheme: 'gold',
          percentage,
          passed: true,
          feedbackMessage: 'Muborakbod etamiz! Ushbu ball bilan nufuzli OTMlarga davlat granti asosida kirish imkoniyatingiz juda yuqori!',
        };
      }
      if (scaled189 >= 110) {
        return {
          categoryType: 'DTM_MOCK',
          scoreDisplay: `${scaled189} ball`,
          title: 'To‘lov-kontrakt ehtimoli',
          subtitle: '189 balldan yaxshi ko‘rsatkich',
          tier: 'GOOD',
          colorTheme: 'blue',
          percentage,
          passed: true,
          feedbackMessage: 'Yaxshi natija! Kontraktga kirish imkoni yuqori, ozgina kuch qo‘shsangiz grantga yetasiz!',
        };
      }
      if (scaled189 >= 56.7) {
        return {
          categoryType: 'DTM_MOCK',
          scoreDisplay: `${scaled189} ball`,
          title: 'O‘tish chegarasida',
          subtitle: 'Tabaqalashtirilgan o‘tish hududi',
          tier: 'AVERAGE',
          colorTheme: 'amber',
          percentage,
          passed: true,
          feedbackMessage: 'Minimal ostonadan o‘tdingiz, biroq xavfsiz kirish uchun ballni oshirish lozim.',
        };
      }
      return {
        categoryType: 'DTM_MOCK',
        scoreDisplay: `${scaled189} ball`,
        title: 'Minimal ostonadan past',
        subtitle: '56.7 balldan past ko‘rsatkich',
        tier: 'NEEDS_WORK',
        colorTheme: 'rose',
        percentage,
        passed: false,
        feedbackMessage: 'Minimal ostonaga yetmadi. Asosiy blok fanlarini jiddiy takrorlash tavsiya etiladi.',
      };
    }

    // Single subject DTM test
    if (percentage >= 86) {
      return {
        categoryType: 'DTM_SINGLE',
        scoreDisplay: null,
        title: "A'lo natija / Maksimalga yaqin",
        subtitle: 'Ushbu fandan blokda maksimal ball olishga tayyorsiz',
        tier: 'EXCELLENT',
        colorTheme: 'emerald',
        percentage,
        passed: true,
        feedbackMessage: 'Qoyilmaqom natija! Ushbu fanni chuqur o‘zlashtirgansiz, asosiy imtihonda ham shunday davom eting!',
      };
    }
    if (percentage >= 70) {
      return {
        categoryType: 'DTM_SINGLE',
        scoreDisplay: null,
        title: 'Yaxshi natija',
        subtitle: 'Biroz mashq bilan maksimal ballga chiqish mumkin',
        tier: 'GOOD',
        colorTheme: 'blue',
        percentage,
        passed: true,
        feedbackMessage: 'Yaxshi ko‘rsatkich! Bir nechta xatoliklar ustida ishlab, 100% natijaga chiqishingiz mumkin.',
      };
    }
    if (percentage >= 50) {
      return {
        categoryType: 'DTM_SINGLE',
        scoreDisplay: null,
        title: 'O‘rtacha daraja',
        subtitle: 'Mavzularni yana bir bor takrorlash tavsiya etiladi',
        tier: 'AVERAGE',
        colorTheme: 'amber',
        percentage,
        passed: true,
        feedbackMessage: 'Asosiy tushunchalar bor, biroq murakkab savollarda ko‘proq amaliyot talab etiladi.',
      };
    }
    return {
      categoryType: 'DTM_SINGLE',
      scoreDisplay: null,
      title: 'Ko‘proq amaliyot kerak',
      subtitle: 'Ushbu fanning zaif mavzulari ustida chuqurroq ishlash zarur',
      tier: 'NEEDS_WORK',
      colorTheme: 'rose',
      percentage,
      passed: false,
      feedbackMessage: 'Ushbu fandan ko‘proq testlar yechib, xatolar tahlili ustida ishlashingiz tavsiya etiladi.',
    };
  }

  // 6. Foreign Languages (CEFR)
  if (canonicalCategory === 'languages') {
    const cefr = percentage >= 90 ? 'C2' : percentage >= 80 ? 'C1' : percentage >= 65 ? 'B2' : percentage >= 50 ? 'B1' : percentage >= 40 ? 'A2' : 'A1';
    const tier: CategoryScoreTier = percentage >= 80 ? 'EXCELLENT' : percentage >= 65 ? 'GOOD' : percentage >= 50 ? 'AVERAGE' : 'NEEDS_WORK';
    return {
      categoryType: 'LANGUAGES',
      scoreDisplay: `CEFR ${cefr}`,
      title: `CEFR ${cefr} darajasi`,
      subtitle: `${percentage}% o‘zlashtirish`,
      tier,
      colorTheme: tier === 'EXCELLENT' ? 'emerald' : 'blue',
      percentage,
      passed: input.passed ?? percentage >= 50,
      feedbackMessage: `Til bilish darajangiz ${cefr} ga baholandi. Muntazam so‘z boyligi va grammatika ustida ishlang!`,
    };
  }

  // 7. General tests
  const tier: CategoryScoreTier = percentage >= 85 ? 'EXCELLENT' : percentage >= 70 ? 'GOOD' : percentage >= 50 ? 'AVERAGE' : 'NEEDS_WORK';
  return {
    categoryType: 'GENERAL',
    scoreDisplay: null,
    title: tier === 'EXCELLENT' ? "A'lo natija" : tier === 'GOOD' ? 'Yaxshi natija' : tier === 'AVERAGE' ? 'Qoniqarli' : 'Ko‘proq tayyorgarlik kerak',
    subtitle: input.passed ? 'Testdan muvaffaqiyatli o‘tdingiz' : 'Qayta urinib ko‘ring',
    tier,
    colorTheme: tier === 'EXCELLENT' ? 'gold' : tier === 'GOOD' ? 'blue' : tier === 'AVERAGE' ? 'amber' : 'rose',
    percentage,
    passed: input.passed ?? percentage >= 60,
    feedbackMessage: tier === 'EXCELLENT'
      ? 'Ajoyib natija! Bilimlaringiz juda yuqori darajada mustahkamlangan!'
      : tier === 'GOOD'
        ? 'Yaxshi natija! Yana bitta sinov orqali natijani yanada oshiring.'
        : 'Xatolar ustida ishlab, testni yana bir bor mustahkamlab oling.',
  };
}
